import { useInvalidateListQueriesFor } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';

import { useRouter } from '../../../context/router/useRouter';
import {
  AbsenceRequest,
  AttendanceCode,
  AttendanceEntriesCreateRequest,
  AttendanceSessionEnum,
  GET_GROUP_ATTENDANCE_AGGREGATE_QUERY,
  GetGroupAttendanceAggregateResponse,
  GroupMember,
  GroupWithMembers,
  JustificationBase,
  JustificationSave,
  useGetAttendanceCodesQuery,
  useTakeAttendanceForGroupMutation,
} from '@schooly/api';
import { allowRenderStudents } from '../../../helpers/renderRoutesByPermissions';
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { useAuth } from '@schooly/components/authentication';
import { useQueryClient } from '@tanstack/react-query';
import {
  AnalyticsEventName,
  AttendanceRecordSource,
  formatAttendanceSessionForTracking,
  trackAmplitudeEvent,
} from '@schooly/analytics';

import { useAttendanceNotificationsEnabled } from '../../../hooks/useAttendanceNotificationsEnabled';
import { hasNotification, requiresJustificationConfirm } from '../../Attendance/attendance-utils';
import { useTakeAttendanceForGroup } from './useTakeAttendanceForGroup';
import { AttendanceGridTrackingPoint } from '../../Attendance/AttendanceTable/common/types';
import {
  ActiveTrackingPoint,
  buildRowEntries,
  buildSaveRelations,
  EntriesByPoint,
  isAllMarkedForPoint,
  pickSessionEntries,
  pickTargetSessions,
  pointSendsNotifications,
  readTakeAttendanceTarget,
  TakeAttendanceEntry,
} from './take-attendance-utils';

export type { TakeAttendanceEntry } from './take-attendance-utils';

export const CODE_CIRCLE_SIZE = 20;

type CodeOption = { label: string; id: string; avatar: ReactNode; withNotification: boolean };

type TakeAttendanceModalContext = {
  dayAggregate: GetGroupAttendanceAggregateResponse | undefined;
  isoDate: string;
  groupDetails: GroupWithMembers | undefined;
  attendanceFetching: boolean;
  canTakeAttendance: boolean;
  activePoint: ActiveTrackingPoint | null;
  trackingPoints: AttendanceGridTrackingPoint[];
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  rows: TakeAttendanceListRow[];
  codes: AttendanceCode[];
  codeOptions: CodeOption[][];
  onSave: () => void;
  onUpdateAttendanceEntry: (
    v: TakeAttendanceEntry,
    justificationNote: JustificationSave | null,
  ) => void;
  markBlankAs: (codeId: string) => void;
  allMarked: boolean;
  isSaving: boolean;
  isModified: boolean;
  canViewStudents: boolean;
};

const noop = () => {};

const TakeAttendanceModalContext = createContext<TakeAttendanceModalContext>({
  dayAggregate: undefined,
  isoDate: '',
  groupDetails: undefined,
  attendanceFetching: true,
  canTakeAttendance: false,
  activePoint: null,
  trackingPoints: [],
  query: '',
  setQuery: noop as Dispatch<SetStateAction<string>>,
  rows: [],
  codes: [],
  codeOptions: [],
  onSave: noop,
  onUpdateAttendanceEntry: noop,
  markBlankAs: noop,
  allMarked: false,
  isSaving: false,
  isModified: false,
  canViewStudents: false,
});

export const TakeAttendanceModalContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { schoolId = '', permissions } = useAuth();
  const canViewStudents = allowRenderStudents(permissions);

  const { data: codes = [] } = useGetAttendanceCodesQuery(schoolId);
  const notificationsEnabled = useAttendanceNotificationsEnabled();

  // The search params carry the tracking point: a session, a period, or both
  // when a Registration + Lesson slot is coupled to a session.
  const [searchParams] = useSearchParams();
  const { session, periodId } = readTakeAttendanceTarget(searchParams);
  const target = useMemo(
    () => ({ session: session ?? (periodId ? null : AttendanceSessionEnum.Am), periodId }),
    [session, periodId],
  );

  const {
    isoDate,
    groupDetails,
    students,
    aggregate,
    trackingPoints,
    activePoint,
    registerId,
    canTakeAttendanceBase,
    fetching: attendanceFetching,
  } = useTakeAttendanceForGroup(target);

  const { showError, showNotification } = useNotifications();
  const { goBack } = useRouter();
  const invalidateListQueries = useInvalidateListQueriesFor('attendanceList');
  const [rows, setRows] = useState<TakeAttendanceListRow[]>([]);
  const initialRowsRef = useRef<TakeAttendanceListRow[]>([]);
  const rowsRef = useRef<TakeAttendanceListRow[]>([]);
  rowsRef.current = rows;
  const queryClient = useQueryClient();
  const takeAttendanceForGroup = useTakeAttendanceForGroupMutation();

  const [query, setQuery] = useState('');

  const trackAttendanceRecord = useCallback(
    (name: AnalyticsEventName.AttendanceTake | AnalyticsEventName.AttendanceEditRecord) => {
      if (!activePoint) return;

      const orderLabel = trackingPoints.find((point) => point.key === activePoint.key)?.period
        ?.label;
      const periodOrder = orderLabel ? Number(orderLabel) : null;
      const sessionValue = formatAttendanceSessionForTracking(
        activePoint.session,
        periodOrder != null ? [periodOrder] : [],
      );

      if (!sessionValue) return;

      trackAmplitudeEvent({
        name,
        payload: { source: AttendanceRecordSource.TakeAttendance, session: sessionValue },
      });
    },
    [activePoint, trackingPoints],
  );

  const handleUpdateAttendanceEntry = useCallback(
    (v: TakeAttendanceEntry, justificationNote: JustificationSave | null) => {
      const previous = rowsRef.current.find((row) => row.student.user.relation_id === v.relation_id)
        ?.entries[v.point_key];

      if (previous && !previous.attendance_code_id && v.attendance_code_id) {
        trackAttendanceRecord(AnalyticsEventName.AttendanceTake);
      } else if (
        previous?.attendance_code_id &&
        v.attendance_code_id &&
        previous.attendance_code_id !== v.attendance_code_id
      ) {
        trackAttendanceRecord(AnalyticsEventName.AttendanceEditRecord);
      }

      setRows((rows) =>
        rows.map((row) =>
          row.student.user.relation_id === v.relation_id
            ? { ...row, entries: { ...row.entries, [v.point_key]: v }, justificationNote }
            : row,
        ),
      );
    },
    [trackAttendanceRecord],
  );

  const handleMarkBlankAs = useCallback(
    (codeId: string) => {
      if (!activePoint) return;

      const newCode = codes.find((code) => code.id === codeId);
      if (!newCode) return;

      const nextRows = rowsRef.current.map((row) => {
        const entry = row.entries[activePoint.key];
        if (!entry || entry.attendance_code_id) return row;

        if (
          requiresJustificationConfirm({
            newCode,
            hasAbsenceNotice: !!row.absence_request?.comment,
            status: row.justification?.status,
            codes,
            entries: pickSessionEntries(row.entries),
            sessions: pickTargetSessions(activePoint),
          })
        ) {
          return row;
        }

        trackAttendanceRecord(AnalyticsEventName.AttendanceTake);

        return {
          ...row,
          entries: {
            ...row.entries,
            [activePoint.key]: { ...entry, attendance_code_id: codeId },
          },
        };
      });

      setRows(nextRows);
    },
    [activePoint, codes, trackAttendanceRecord],
  );

  const handleSave = useCallback(() => {
    if (!groupDetails || !registerId || !activePoint) return;

    const relations = buildSaveRelations(rows, activePoint.key);

    // A coupled Registration + Lesson slot sends both `session` and `period_id`.
    const data: AttendanceEntriesCreateRequest = {
      register_id: registerId,
      relations,
      session: activePoint.session,
      period_id: activePoint.period_id,
    };

    takeAttendanceForGroup.mutate(
      { groupId: groupDetails.id, data },
      {
        onSuccess: () => {
          goBack();
          invalidateListQueries();
          showNotification({ textId: 'attendance-Saved' });
          queryClient.invalidateQueries([GET_GROUP_ATTENDANCE_AGGREGATE_QUERY]);
        },
        onError: showError,
      },
    );
  }, [
    groupDetails,
    registerId,
    activePoint,
    rows,
    takeAttendanceForGroup,
    goBack,
    invalidateListQueries,
    showNotification,
    queryClient,
    showError,
  ]);

  const sendsNotifications = pointSendsNotifications(activePoint);

  const codeOptions = useMemo(() => {
    const codeOptions = codes.reduce<Array<CodeOption[]>>(
      (acc, code) => {
        const option = {
          label: code.name,
          id: code.id,
          withNotification: sendsNotifications && hasNotification(code, notificationsEnabled),
          avatar: <AttendanceCodeCircle code={code} size={CODE_CIRCLE_SIZE} />,
        };

        acc[code.is_present ? 0 : 1].push(option);
        return acc;
      },
      [[], []],
    );

    return codeOptions;
  }, [codes, notificationsEnabled, sendsNotifications]);

  const initialRows = useMemo(
    () => buildTakeAttendanceRows(students, aggregate, trackingPoints),
    [students, aggregate, trackingPoints],
  );

  useEffect(() => {
    if (areRowsModified(rowsRef.current, initialRowsRef.current)) return;
    setRows(initialRows);
    initialRowsRef.current = initialRows;
  }, [initialRows]);

  const isModified = useMemo(() => areRowsModified(rows, initialRowsRef.current), [rows]);

  const activeKeyForCheck = activePoint?.key ?? '';
  const allMarked = useMemo(
    () =>
      isAllMarkedForPoint(
        rows.map((row) => row.entries),
        activeKeyForCheck,
      ),
    [rows, activeKeyForCheck],
  );

  return (
    <TakeAttendanceModalContext.Provider
      value={{
        dayAggregate: aggregate,
        isModified,
        isSaving: takeAttendanceForGroup.isLoading,
        codeOptions,
        codes,
        rows,
        query,
        setQuery,
        activePoint,
        trackingPoints,
        isoDate,
        groupDetails,
        attendanceFetching,
        onSave: handleSave,
        onUpdateAttendanceEntry: handleUpdateAttendanceEntry,
        markBlankAs: handleMarkBlankAs,
        allMarked,
        canTakeAttendance: canTakeAttendanceBase,
        canViewStudents,
      }}
    >
      {children}
    </TakeAttendanceModalContext.Provider>
  );
};

export const useTakeAttendanceModal = () => {
  return useContext(TakeAttendanceModalContext);
};

const entryKey = (entry: TakeAttendanceEntry | undefined) =>
  `${entry?.attendance_code_id ?? ''}|${entry?.text ?? ''}`;

const areRowsModified = (
  rows: TakeAttendanceListRow[],
  initialRows: TakeAttendanceListRow[],
): boolean =>
  rows.some((row, index) => {
    const initial = initialRows[index];
    if (!initial) return true;

    const keys = new Set([...Object.keys(row.entries), ...Object.keys(initial.entries)]);
    return [...keys].some((key) => entryKey(row.entries[key]) !== entryKey(initial.entries[key]));
  });

export type TakeAttendanceListRow = {
  student: GroupMember;
  entries: EntriesByPoint;
  justification: JustificationBase | null;
  justificationNote: JustificationSave | null;
  absence_request: AbsenceRequest | null;
};

export const buildTakeAttendanceRows = (
  students: GroupMember[],
  aggregate: GetGroupAttendanceAggregateResponse | undefined,
  points: AttendanceGridTrackingPoint[],
): TakeAttendanceListRow[] => {
  const rowByRelation = new Map((aggregate?.rows ?? []).map((row) => [row.meta.relation_id, row]));

  return students.map((student) => {
    const relationId = student.user.relation_id;
    const aggregateRow = rowByRelation.get(relationId) ?? {
      meta: { relation_id: relationId },
      cells: [],
    };
    const justificationCell = aggregateRow.cells.find((cell) => cell.value.justification);
    const absenceCell = aggregateRow.cells.find((cell) => cell.value.absence_request);

    return {
      student,
      entries: buildRowEntries(aggregateRow, points),
      justification: justificationCell?.value.justification ?? null,
      absence_request: absenceCell?.value.absence_request ?? null,
      justificationNote: null,
    };
  });
};
